import { ComponentProps, useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { SplitListingModal } from 'src/modals/SplitListing';
import { DownloadIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { downloadFileFromBlob } from 'src/utils/fileUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { ListingClient } from 'src/WebApiController';

export const LaunchDownloadUserDocuments = ({
  listingId,
  variant,
  iconOnlyMode,
  disabled,
  ...buttonProps
}: {
  listingId: number;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
} & ComponentProps<typeof SplitListingModal>) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const [isLoading, setIsLoading] = useState(false);

  const onDownload = useCallback(async () => {
    setIsLoading(true);
    return await tryInvokeApi(
      async () => {
        const result = await new ListingClient(
          activeAccountWebClientConfig
        ).downloadMergedUserDocumentsPdf(listingId);
        if (result.data) {
          downloadFileFromBlob(result.fileName ?? 'tickets.pdf', result.data);
        }
      },
      (error) => {
        showErrorDialog('ListingClient.downloadMergedUserDocumentsPdf', error);
      },
      () => setIsLoading(false)
    );
  }, [activeAccountWebClientConfig, listingId, showErrorDialog]);

  const onClickHandler = useCallback(
    (e: React.MouseEvent) => {
      onDownload();
    },
    [onDownload]
  );

  const iconFill =
    iconOnlyMode || (variant && variant !== 'regular')
      ? vars.color.textBrand
      : vars.color.textInverted;

  return (
    <ButtonWithIcon
      {...buttonProps}
      disabled={disabled || isLoading}
      variant={variant}
      textColor={variant === 'textPlain' ? 'strong' : 'brand'}
      style={variant === 'textPlain' ? { width: '100%' } : undefined}
      justifyContent="space-between"
      iconOnlyMode={iconOnlyMode}
      onClick={onClickHandler}
      textContentId={ContentId.DownloadETickets}
      icon={
        isLoading ? (
          <RotatingWrapper>
            <ProcessingIcon fill={iconFill} />
          </RotatingWrapper>
        ) : (
          <DownloadIcon
            withHoverEffect
            fill={iconFill}
            size={vars.iconSize.m}
          />
        )
      }
    />
  );
};
